import { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  ArrowRight,
  Moon,
  Sun,
} from "lucide-react";
import "./Footer.css";
import { ThemeContext } from "../../../context/ThemeContext";
import SquaayLogo from "../../../utils/svgs/SquaayLogo";

const Footer = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [downloadLink, setDownloadLink] = useState("");

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();

    const isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      (ua.includes("mac") && "ontouchend" in document);

    if (isIOS) {
      setDownloadLink("https://apps.apple.com/app/squaay/id6740501959");
    } else {
      setDownloadLink(
        "https://play.google.com/store/apps/details?id=com.squaay.android",
      );
    }
  }, []);

  const footerSections = [
    {
      title: "Product",
      links: [
        { name: "Download App", href: downloadLink },
        { name: "Features", href: "/features" },
        { name: "Safety", href: "/safety-guidelines" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Terms of Service", href: "/terms" },
        { name: "Privacy Policy", href: "/privacy-policy" },
        // { name: "Cookie Policy", href: "#" },
      ],
    },
    {
      title: "Company",
      links: [
        // { name: "About Us", href: "/about" },
        // { name: "Careers", href: "#" },
        { name: "Blog", href: "/blog" },
        // { name: "Newsroom", href: "#" },
      ],
    },
    {
      title: "Support",
      links: [
        { name: "Help Center", href: "/help-center" },
        // { name: "Contact Us", href: "/help-center" },
        // { name: "FAQ", href: "/help-center" },
        // { name: "Report Issue", href: "/help-center" },
      ],
    },
  ];

  const socialLinks = [
    {
      icon: Facebook,
      label: "Facebook",
      url: "https://www.facebook.com/squaay",
    },
    {
      icon: Twitter,
      label: "Twitter",
      url: "https://www.twitter.com/squaayapp",
    },
    {
      icon: Instagram,
      label: "Instagram",
      url: "https://www.instagram.com/squaay",
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      url: "https://www.linkedin.com/company/squaay",
    },
  ];

  return (
    <footer className="footer">
      <div className="footer-background">
        <div className="footer-gradient"></div>
        <div className="footer-blob blob-footer-1"></div>
        <div className="footer-blob blob-footer-2"></div>
      </div>

      <div className="footer-wrapper">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">
              <div className="logo-text">
                <Link to="/">
                  <SquaayLogo
                    width={100}
                    primaryColor={theme === "dark" ? "#ffffff" : "#2B2320"}
                  />
                </Link>
                <p>Social Food Delivery</p>
              </div>
            </div>
            <p className="footer-description">
              Connecting food lovers, supporting local businesses, and
              empowering creators to build meaningful food experiences together.
            </p>

            <div className="footer-contact">
              <a href="mailto:support@squaay.com" className="contact-item">
                <Mail size={18} />
                <span>support@squaay.com</span>
              </a>
              {/* <a href="tel:+2348084082347" className="contact-item">
                <Phone size={18} />
                <span>+234 (808) 408-2347</span>
              </a> */}
              <button
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                title={
                  theme === "dark"
                    ? "Switch to Light Mode"
                    : "Switch to Dark Mode"
                }
              >
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          </div>

          <div className="footer-links-grid">
            {footerSections.map((section, index) => (
              <div key={index} className="footer-section">
                <h4 className="footer-section-title">{section.title}</h4>
                <ul className="footer-links-list">
                  {section.links.map((link, idx) => (
                    <li key={idx}>
                      {link.href.startsWith("http") ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-link"
                        >
                          <span>{link.name}</span>
                          <ArrowRight size={14} className="link-arrow" />
                        </a>
                      ) : (
                        <Link to={link.href} className="footer-link">
                          <span>{link.name}</span>
                          <ArrowRight size={14} className="link-arrow" />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-social">
            <h4>Follow Us</h4>
            <div className="social-links">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={social.label}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
            <div className="footer-credits">
              <p>
                &copy; {new Date().getFullYear()} Squaay Ltd. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
